import CustomStore from 'devextreme/data/custom_store';
import type {
  ReportingComparisonRowDto,
  ReportingComparisonRowsHttpBody,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import type { ComparisonGridLoadOptions } from './production-comparison-table.state';
import type { ProductionComparisonRowsService } from './production-comparison-rows.service';

/**
 * A comparison table as a DevExtreme store.
 *
 * The grid's own paging, sorting and filtering then act on the whole set rather than on the
 * rows that happen to be loaded — which is the entire point of these tables. A filter applied
 * to a loaded page would narrow twenty-five rows and quietly ignore the other hundred.
 *
 * `buildBody` is read on every load rather than captured, so a table follows the page's date
 * range and MGA filter without being rebuilt.
 */
export function createComparisonRowsStore(
  rowsService: ProductionComparisonRowsService,
  buildBody: () => Omit<ReportingComparisonRowsHttpBody, 'loadOptions'>,
): CustomStore {
  return new CustomStore({
    key: 'entityName',
    loadMode: 'processed',
    load: async loadOptions => {
      const response = await rowsService.getRows({
        ...buildBody(),
        loadOptions: loadOptions as Record<string, unknown>,
      });

      return { data: response.data, totalCount: response.totalCount };
    },
  });
}

/**
 * What a mover store needs of the table behind it — its rows, and somewhere to put the sort.
 *
 * Declared here rather than imported as the class so the two files do not depend on each other
 * in both directions.
 */
export interface ComparisonMoverRowsSource {
  rows: ReportingComparisonRowDto[];
  totalCount: number;
  applyGridLoadOptions(loadOptions: ComparisonGridLoadOptions): Promise<void>;
  loadGroupCounts(loadOptions: ComparisonGridLoadOptions): Promise<unknown[]>;
}

/**
 * A mover table as a DevExtreme store.
 *
 * The table loads its own rows a page at a time and Load more grows them, so this does not
 * fetch: it relays what the viewer clicked to the table and hands the grid back whatever the
 * table now holds.
 *
 * It exists so the grid stops ordering rows itself. Bound to a plain array the grid sorted the
 * five rows in front of it, against the order the server had been asked for — a table headed
 * by its top five showing the smallest five, because a stored column said ascending while the
 * request still said descending.
 */
export function createComparisonMoverStore(source: ComparisonMoverRowsSource): CustomStore {
  return new CustomStore({
    key: 'entityName',
    loadMode: 'processed',
    load: async loadOptions => {
      const options = loadOptions as ComparisonGridLoadOptions;

      // A header filter asks for a column's values, not for rows, and must not be mistaken for
      // the table being reordered or narrowed. It is answered on its own.
      if (options?.group) {
        return source.loadGroupCounts(options);
      }

      await source.applyGridLoadOptions(options);

      // The count is the whole set's, not the handful being handed over, and it is not
      // optional: remote paging has the grid refuse a response without one. The grid's own
      // pager is off, so this feeds nothing but its idea of how much it is looking at.
      return { data: source.rows, totalCount: source.totalCount };
    },
  });
}
